/**
 * Spatial repeatability measurement, as a numbered sequence of steps.
 *
 * The palette is marked repeatedly across the plate, photographed per capture
 * mode, and measured - which is what the repeatability filter then judges. The
 * palette is cut into batches of at most one camera field, so a large palette
 * becomes several plates and several photos.
 *
 * Each photo is sent to the server once, when it is picked; the server stores it,
 * detects the corner crosses and returns the preview, and from then on only the
 * corner points travel. The test is optional: the selection can be finished
 * without it.
 */
import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
    Stack, Button, Modal, Box as MuiBox, Typography, Paper, CircularProgress,
    LinearProgress, Box, Alert, Chip, FormControlLabel,
    Radio, RadioGroup, Tabs, Tab,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from "@mui/material";
import NavBarBottom from "../../components/NavBarBottom";
import ExplorationPageShell from "../../components/ExplorationPageShell";
import LaserMarkPanel from "../../components/LaserMarkPanel";
import MarkingTimeChip from "../../components/MarkingTimeChip";
import { sumMarkingTimes } from "../../helper/markingTime";
import CameraCapturePanel from "../../components/CameraCapturePanel";
import PlateMatrix, { plateLabel as plateName } from "../../components/PlateMatrix";
import { modeLabel } from "../../helper/repeatabilityText";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import SaveIcon from "@mui/icons-material/Save";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import axios from "axios";
import {
    naturalToDisplayedImageCoords,
    displayedToNaturalImageCoords,
} from "../../helper/imageOverlayCoordinates";
import {
    selectionSidebar, selectionLockedRoute, isModeContrastSelection,
} from "../../helper/selectionFlow";
import { useSelectionParams } from "../../helper/entityRoutes";

const actionButtonSx = {
    textTransform: "none",
    borderRadius: 2,
    px: 2.5,
    py: 1,
    fontWeight: 600,
    boxShadow: "none",
    "&:hover": { boxShadow: 1 },
};

const stepCardSx = {
    width: "100%",
    minWidth: 0,
    maxWidth: "100%",
    m: 0,
    boxSizing: "border-box",
    p: { xs: 2, sm: 2.5 },
    borderRadius: 2,
    border: "1px solid",
    borderColor: "divider",
    bgcolor: "background.paper",
};

/** Numbered step badge — matches the exploration measurement step cards. */
const stepNumberCircleSx = {
    minWidth: 28,
    height: 28,
    borderRadius: "50%",
    bgcolor: "primary.main",
    color: "primary.contrastText",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    typography: "subtitle2",
    fontWeight: 700,
};


/** Client-side view of one photo set (the server row is in `captures`). */
function emptySlotState() {
    return {
        preview: "",            // object URL of the downscaled PNG
        scalingFactors: null,   // preview px -> raw px
        cornerPoints: [],       // preview coordinates {x_a, y_a}
        busy: false,
        previewMessage: "",
        savingCorners: false,
    };
}

/** One photo set = one plate (batch x repetition) in one capture mode. */
function slotKey(batch, repetition, mode) {
    return `${batch}-${repetition}-${mode}`;
}

function samePlate(a, b) {
    return Boolean(a && b) && a.batch === b.batch && a.repetition === b.repetition;
}

function StepHeader({ number, title, children }) {
    return (
        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ width: "100%" }}>
            <Box sx={stepNumberCircleSx}>{number}</Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
                {children}
            </Box>
        </Stack>
    );
}

function pngObjectUrl(latin1) {
    const bytes = new Uint8Array(latin1.split("").map((char) => char.charCodeAt(0)));
    return URL.createObjectURL(new Blob([bytes], { type: "image/png" }));
}

// Step 5: spatial repeatability of the composed palette. The palette is
// marked in batches of at most one camera field (10 x 10 patches); every
// batch is marked once per repetition, and each of those plates is one
// marking file and one photo per capture mode. The page shows the plates as
// a batch x repetition matrix and ONE photo slot at a time: after a
// successful upload the slot moves on to the next plate, so a long palette
// (5 batches x 2 repetitions x 2 modes) never stacks 20 previews on the
// page. Every photo is sent to the server ONCE, the moment it is picked:
// the server stores it at its final location, detects the corner crosses on
// it and returns the preview. From then on only corner points travel. The
// Spatial Repeatability Filter (3D gamut, by-eye rejection, confirm) is the
// next page. The test is OPTIONAL: the selection can be finished without it
// (api/selection/spatial/skip), keeping the composed palette as is.
export default function ColorSelectionSpatial() {
    /** SelectionMarkingFiles rows (the plates), ordered (batch, repetition). */
    const [markingRows, setMarkingRows] = useState([]);
    /** Per batch: patch count + grid ([] until the server reports it). */
    const [batches, setBatches] = useState([]);
    /** Server state of every photo set + which modes have a normalization image. */
    const [captures, setCaptures] = useState([]);
    const capturesRef = useRef(captures);
    capturesRef.current = captures;
    const [normOnServer, setNormOnServer] = useState({});
    /** Capture settings as saved on the server, and the form being edited. */
    const [savedSettings, setSavedSettings] = useState({ modes: 1, limits: [4] });
    const [modes, setModes] = useState(1);
    const [savingSettings, setSavingSettings] = useState(false);

    const [slots, setSlots] = useState({});
    const slotsRef = useRef(slots);
    slotsRef.current = slots;
    const slotImageRefs = useRef({});
    /** Normalization image per mode: file name + busy flag. */
    const [norms, setNorms] = useState({});

    const [generating, setGenerating] = useState(false);
    const [preparing, setPreparing] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [sampleCount, setSampleCount] = useState(null);
    const [selectionStatus, setSelectionStatus] = useState(null);
    /** Plate whose marking file the laser panel sends. */
    const [markPlate, setMarkPlate] = useState(null);
    /** Capture mode shown in the photo step, and the plate in its slot (per mode). */
    const [photoMode, setPhotoMode] = useState(1);
    const [activePlates, setActivePlates] = useState({});
    const [modalSlotKey, setModalSlotKey] = useState(null);
    const [autoCornersBusy, setAutoCornersBusy] = useState(false);
    const [measuring, setMeasuring] = useState(false);
    /** A photo set changed since the last measurement (forces a re-measure on Next). */
    const [photosChanged, setPhotosChanged] = useState(false);
    const [skipDialogOpen, setSkipDialogOpen] = useState(false);
    const [skipping, setSkipping] = useState(false);
    // Bumped after image load so overlays account for object-fit: contain.
    const [, setThumbLayoutTick] = useState(0);
    const [, setModalLayoutTick] = useState(0);
    const modalImageRef = useRef(null);
    const navigate = useNavigate();

    const { selectionId, selectionType: urlType, stepRoute } = useSelectionParams();
    const [selectionType, setSelectionType] = useState(urlType);
    // The mode contrast selection compares the two capture modes: always two, never skipped.
    const modeContrastType = isModeContrastSelection(selectionType);

    const updateSlot = (key, partial) => {
        setSlots((prev) => ({ ...prev, [key]: { ...(prev[key] ?? emptySlotState()), ...partial } }));
    };
    const updateNorm = (mode, partial) => {
        setNorms((prev) => ({ ...prev, [mode]: { ...(prev[mode] ?? {}), ...partial } }));
    };

    const applySettings = (payload) => {
        const nextModes = Number(payload.modes) || 1;
        const nextLimits = (payload.limits || []).map(Number);
        setSavedSettings({ modes: nextModes, limits: nextLimits });
        setModes(nextModes);
        setPhotoMode((current) => Math.min(current, nextModes));
    };

    const applyBatches = (payload) => {
        if (Array.isArray(payload?.batches) && payload.batches.length > 0) setBatches(payload.batches);
    };

    const normPreviewRequested = useRef({});
    const loadNormPreview = async (mode) => {
        if (normPreviewRequested.current[mode]) return;
        normPreviewRequested.current[mode] = true;
        try {
            const response = await axios.post('api/selection/spatial/normalization/preview', { selectionId, mode }, { timeout: 600000 });
            if (response.data?.image) updateNorm(mode, { preview: pngObjectUrl(response.data.image) });
        } catch (error) {
            console.error('Error loading normalization preview:', error);
        }
    };

    const applyCaptureState = (data) => {
        if (Array.isArray(data?.captures)) setCaptures(data.captures);
        if (data?.normalization) {
            setNormOnServer(data.normalization);
            // thumbnails of the normalization images already on the server
            Object.keys(data.normalization).forEach((mode) => loadNormPreview(Number(mode)));
        }
        applyBatches(data);
    };

    const replaceCapture = (capture) => {
        if (!capture) return;
        setCaptures((prev) => [
            ...prev.filter((c) => !(c.batch === capture.batch && c.repetition === capture.repetition && c.mode === capture.mode)),
            capture,
        ]);
    };

    // The normalization image and the plate photos upload in parallel; each
    // answer only knows the server state of its own moment. After every
    // upload the page re-reads the capture list; an older answer that
    // arrives after a newer one is ignored.
    const capturesRequest = useRef(0);
    const reconcileCaptures = async () => {
        const id = capturesRequest.current + 1;
        capturesRequest.current = id;
        try {
            const response = await axios.post('api/selection/spatial/captures', { selectionId });
            if (id === capturesRequest.current) applyCaptureState(response.data);
        } catch (error) {
            console.error('Error fetching capture state:', error);
        }
    };

    const refreshCaptures = async () => {
        try {
            const response = await axios.post('api/selection/spatial/captures', { selectionId });
            applyCaptureState(response.data);
            applySettings(response.data);
            return response.data;
        } catch (error) {
            console.error('Error fetching capture state:', error);
            return null;
        }
    };

    const refreshMarkingRows = async () => {
        try {
            const selectionResponse = await axios.get('api/color/selection/' + selectionId + "/");
            const locked = selectionLockedRoute(
                selectionId, selectionResponse.data.status,
                selectionResponse.data.selection_type || selectionType,
            );
            if (locked) {
                // Finished (or repeatability-confirmed) selections: continue there.
                navigate(locked, { replace: true });
                return [];
            }
            if (selectionResponse.data.selection_type) setSelectionType(selectionResponse.data.selection_type);
            setSelectionStatus(selectionResponse.data.status);
            if (Array.isArray(selectionResponse.data.selected_samples)) {
                setSampleCount(selectionResponse.data.selected_samples.length);
            }
            const rowsResponse = await axios.get('api/color/selection/marking/file/?selection=' + selectionId);
            const rows = (rowsResponse.data || []).slice().sort(
                (a, b) => (a.batch - b.batch) || (a.repetition - b.repetition)
            );
            setMarkingRows(rows);
            await refreshCaptures();
            return rows;
        } catch (error) {
            console.error('Error fetching marking rows:', error);
            return [];
        }
    };

    useEffect(() => {
        // Mirror the exploration flow: the marking files are prepared on page
        // load, so the laser panel and the photo slots are usable immediately.
        const init = async () => {
            const rows = await refreshMarkingRows();
            if (rows.length === 0) {
                setPreparing(true);
                try {
                    const response = await axios.post(
                        'api/selection/spatial/marking/prepare',
                        { selectionId },
                        { timeout: 600000 },
                    );
                    applyBatches(response.data);
                    await refreshMarkingRows();
                } catch (error) {
                    console.error("Error preparing marking files:", error);
                    setErrorMessage("Preparing the spatial marking files failed.");
                } finally {
                    setPreparing(false);
                }
            }
        };
        init();
    }, []);

    // ---- plates ----
    const repetitions = markingRows.reduce((n, row) => Math.max(n, row.repetition + 1), 0);
    // Older selections report no batch layout: fall back to the plates' batch numbers.
    const batchRows = batches.length > 0
        ? batches
        : Array.from(new Set(markingRows.map((row) => row.batch))).sort((a, b) => a - b).map((batch) => ({ batch }));
    const plateRow = (plate) => markingRows.find((row) => samePlate(row, plate));
    const captureFor = (plate, mode, list = captures) => list.find(
        (c) => c.batch === plate.batch && c.repetition === plate.repetition && c.mode === mode
    );
    const activeModes = Array.from({ length: savedSettings.modes }, (_, i) => i + 1);
    const completeCountOf = (mode) => markingRows.filter((row) => captureFor(row, mode)?.complete).length;
    const completeCount = activeModes.reduce((n, mode) => n + completeCountOf(mode), 0);
    const totalSets = markingRows.length * savedSettings.modes;
    const readyToMeasure = markingRows.length > 0 && completeCount === totalSets;
    const settingsDirty = modes !== savedSettings.modes;

    /** Tile state of one plate in one mode (icon + tooltip on the matrix). */
    const plateState = (mode) => (batch, repetition) => {
        const capture = captureFor({ batch, repetition }, mode);
        if (capture?.complete) return { state: "complete", hint: "Uploaded" };
        if (capture?.photo) {
            const corners = capture.corner_points?.length ?? 0;
            return {
                state: "partial",
                hint: corners !== 4
                    ? `${corners} of 4 corner points set`
                    : `Normalization image for ${modeLabel(mode).toLowerCase()} missing`,
            };
        }
        return { state: "empty", hint: "No photo yet" };
    };

    /** The next plate (after `from`, cyclic) without a photo in `mode`, or null. */
    const nextPlateWithoutPhoto = (mode, from, list) => {
        if (markingRows.length === 0) return null;
        const start = from ? markingRows.findIndex((row) => samePlate(row, from)) : -1;
        for (let step = 1; step <= markingRows.length; step += 1) {
            const row = markingRows[(start + step) % markingRows.length];
            if (!captureFor(row, mode, list)?.photo) return row;
        }
        return null;
    };

    /** Plate in the photo slot of `mode`: the chosen one, else the first
     *  plate that is not complete yet, else the first plate. */
    const activePlateOf = (mode) => {
        const chosen = activePlates[mode];
        if (chosen && plateRow(chosen)) return plateRow(chosen);
        return markingRows.find((row) => !captureFor(row, mode)?.complete) ?? markingRows[0] ?? null;
    };
    const selectPlate = (mode, plate) => {
        setActivePlates((prev) => ({ ...prev, [mode]: { batch: plate.batch, repetition: plate.repetition } }));
    };

    useEffect(() => {
        if (markPlate == null && markingRows.length > 0) {
            setMarkPlate({ batch: markingRows[0].batch, repetition: markingRows[0].repetition });
        }
    }, [markingRows, markPlate]);

    const saveSettings = async () => {
        setSavingSettings(true);
        setErrorMessage(null);
        try {
            const response = await axios.post('api/selection/spatial/settings', { selectionId, modes });
            applySettings(response.data);
        } catch (error) {
            console.error('Error saving capture settings:', error);
            setErrorMessage(error?.response?.data?.error || "Saving the capture settings failed.");
        } finally {
            setSavingSettings(false);
        }
    };

    const generateAndDownloadZip = async () => {
        setGenerating(true);
        setErrorMessage(null);
        try {
            const response = await axios.post(
                'api/selection/spatial/marking/files',
                { selectionId },
                { responseType: "blob", timeout: 600000 },
            );
            const url = URL.createObjectURL(response.data);
            const link = document.createElement("a");
            link.href = url;
            link.download = "marking-files-spatial.zip";
            link.click();
            URL.revokeObjectURL(url);
            await refreshMarkingRows();
        } catch (error) {
            console.error("Error generating marking files:", error);
            setErrorMessage("Generating/downloading the marking files failed.");
        } finally {
            setGenerating(false);
        }
    };

    const getMarkingBlobForLaser = async () => {
        const row = plateRow(markPlate) ?? markingRows[0];
        const response = await axios.post(
            "api/selection/marking/file/download",
            { selectionId, batch: row.batch, repetition: row.repetition },
            { responseType: "blob" },
        );
        return {
            blob: response.data,
            filename: `marking_rep${row.repetition}_batch${row.batch}.json`,
        };
    };

    // Normalization image of one mode: stored on the server right away and
    // shared by every plate photographed in that mode.
    const processNormFile = async (mode, file) => {
        if (!file) return;
        updateNorm(mode, { busy: true, name: file.name });
        setErrorMessage(null);
        try {
            const formData = new FormData();
            formData.append("selectionId", selectionId);
            formData.append("mode", mode);
            formData.append("file", file);
            const response = await axios.post('api/selection/spatial/normalization', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }, timeout: 600000,
            });
            if (Array.isArray(response.data?.captures)) setCaptures(response.data.captures);
            setNormOnServer((prev) => ({ ...prev, [mode]: true }));
            if (response.data?.image) updateNorm(mode, { preview: pngObjectUrl(response.data.image) });
            setPhotosChanged(true);
            reconcileCaptures();
        } catch (error) {
            console.error('Error storing normalization image:', error);
            setErrorMessage(error?.response?.data?.error || "Storing the normalization image failed.");
        } finally {
            updateNorm(mode, { busy: false });
        }
    };

    const applyPreview = (key, data) => {
        const xScale = parseFloat(data.scaling_factors.x_scale);
        const yScale = parseFloat(data.scaling_factors.y_scale);
        updateSlot(key, {
            preview: pngObjectUrl(data.image),
            scalingFactors: { xScale, yScale },
            cornerPoints: (data.cornerPoints || []).map((point) => ({ x_a: point[0] / xScale, y_a: point[1] / yScale })),
        });
        replaceCapture(data.capture);
    };

    // Plate photo: ONE round-trip. The server stores the RAW at its final
    // location, detects the corners and answers with the preview. A complete
    // photo set moves the slot on to the next plate without a photo.
    const processPlateFileForSlot = async (key, row, mode, file) => {
        if (!file) return;
        updateSlot(key, { busy: true, previewMessage: "Uploading, storing and detecting corner points…", cornerPoints: [] });
        setErrorMessage(null);
        try {
            const formData = new FormData();
            formData.append("selectionId", selectionId);
            formData.append("batch", row.batch);
            formData.append("repetition", row.repetition);
            formData.append("mode", mode);
            formData.append("file", file);
            const response = await axios.post('api/selection/spatial/photo', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }, timeout: 900000,
            });
            applyPreview(key, response.data);
            setPhotosChanged(true);
            reconcileCaptures();
            const capture = response.data?.capture;
            if (capture?.complete) {
                const list = [
                    ...capturesRef.current.filter((c) => !(c.batch === capture.batch && c.repetition === capture.repetition && c.mode === capture.mode)),
                    capture,
                ];
                const next = nextPlateWithoutPhoto(mode, row, list);
                if (next) selectPlate(mode, next);
            }
        } catch (error) {
            console.error("Error storing plate photo:", error);
            setErrorMessage(error?.response?.data?.error || "Storing the plate photo failed.");
        } finally {
            updateSlot(key, { busy: false, previewMessage: "" });
        }
    };

    // Photo already on the server (page reload): fetch its preview + corners.
    const previewRequested = useRef({});
    const loadStoredPreview = async (key, row, mode) => {
        if (previewRequested.current[key]) return;
        previewRequested.current[key] = true;
        updateSlot(key, { busy: true, previewMessage: "Loading the stored photo…" });
        try {
            const response = await axios.post('api/selection/spatial/photo/preview', {
                selectionId, batch: row.batch, repetition: row.repetition, mode,
            }, { timeout: 600000 });
            applyPreview(key, response.data);
        } catch (error) {
            console.error("Error loading stored photo:", error);
            setErrorMessage(error?.response?.data?.error || "Loading the stored photo failed.");
            previewRequested.current[key] = false;
        } finally {
            updateSlot(key, { busy: false, previewMessage: "" });
        }
    };

    // The slot shows one plate: fetch its stored photo when it comes into view.
    const activePlate = activePlateOf(photoMode);
    const activeKey = activePlate ? slotKey(activePlate.batch, activePlate.repetition, photoMode) : null;
    useEffect(() => {
        if (!activePlate || !activeKey) return;
        const capture = captureFor(activePlate, photoMode);
        const slot = slotsRef.current[activeKey];
        if (capture?.photo && !slot?.preview && !slot?.busy) {
            loadStoredPreview(activeKey, activePlate, photoMode);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeKey, captures]);

    // Corner points travel on their own (raw pixels) — a few bytes.
    const saveCorners = async (key, row, mode, options = {}) => {
        const slot = slotsRef.current[key];
        if (!slot) return;
        const { xScale, yScale } = slot.scalingFactors || {};
        const payload = {
            selectionId, batch: row.batch, repetition: row.repetition, mode,
            ...(options.detect
                ? { detect: true }
                : { cornerPoints: slot.cornerPoints.map((p) => [p.x_a * (xScale || 1), p.y_a * (yScale || 1)]) }),
        };
        updateSlot(key, { savingCorners: true });
        try {
            const response = await axios.post('api/selection/spatial/corners', payload, { timeout: 600000 });
            updateSlot(key, {
                cornerPoints: (response.data.cornerPoints || []).map((point) => ({
                    x_a: point[0] / (xScale || 1), y_a: point[1] / (yScale || 1),
                })),
            });
            replaceCapture(response.data.capture);
            setPhotosChanged(true);
        } catch (error) {
            console.error("Error saving corner points:", error);
            setErrorMessage(error?.response?.data?.error || "Saving the corner points failed.");
        } finally {
            updateSlot(key, { savingCorners: false });
        }
    };

    // Next: measure (unless the palette is measured and nothing changed since),
    // then open the review — the exploration pages' idiom.
    const measureSpatialRepeatability = async () => {
        if (selectionStatus === "SPATIAL_MEASURED" && !photosChanged) {
            navigate(stepRoute("repeatability", selectionType));
            return;
        }
        setMeasuring(true);
        setErrorMessage(null);
        try {
            await axios.post('api/selection/filter/spatial', { selectionId }, { timeout: 1800000 });
            navigate(stepRoute("repeatability", selectionType));
        } catch (error) {
            console.error('Error measuring spatial repeatability:', error);
            setErrorMessage(error?.response?.data?.error || "The spatial repeatability measurement failed.");
        } finally {
            setMeasuring(false);
        }
    };

    // Finish the selection without the repeatability test (no plate/laser at
    // hand). The backend records the spatial step as SKIPPED and finalizes.
    const skipSpatialRepeatability = async () => {
        setSkipping(true);
        setErrorMessage(null);
        try {
            await axios.post('api/selection/spatial/skip', { selectionId });
            setSkipDialogOpen(false);
            navigate(stepRoute("overview", selectionType));
        } catch (error) {
            console.error('Error skipping spatial repeatability:', error);
            setSkipDialogOpen(false);
            setErrorMessage(
                error?.response?.data?.error || "Skipping the repeatability test failed."
            );
        } finally {
            setSkipping(false);
        }
    };

    // ---- corner modal ----
    const modalRow = modalSlotKey != null
        ? markingRows.find((row) => modalSlotKey.startsWith(`${row.batch}-${row.repetition}-`))
        : null;
    const modalMode = modalSlotKey != null ? Number(modalSlotKey.split("-")[2]) : null;

    const handleModalImageClick = (event) => {
        const image = modalImageRef.current;
        if (!image || modalSlotKey == null) return;
        const imageRect = image.getBoundingClientRect();
        const { x: x_a, y: y_a } = displayedToNaturalImageCoords(
            image, event.clientX - imageRect.left, event.clientY - imageRect.top
        );
        const prevCorners = slotsRef.current[modalSlotKey]?.cornerPoints ?? [];
        if (prevCorners.length >= 4) return;
        updateSlot(modalSlotKey, { cornerPoints: [...prevCorners, { x_a, y_a }] });
    };

    const handleAutomaticCornerDetection = async () => {
        if (modalSlotKey == null || !modalRow) return;
        setAutoCornersBusy(true);
        try {
            await saveCorners(modalSlotKey, modalRow, modalMode, { detect: true });
        } finally {
            setAutoCornersBusy(false);
        }
    };

    // Closing the modal saves the corners (sorted server-side).
    const closeModal = async () => {
        const key = modalSlotKey;
        const row = modalRow;
        const mode = modalMode;
        setModalSlotKey(null);
        if (key && row) await saveCorners(key, row, mode);
    };

    const cornerDot = (coordinate, imgEl, key) => {
        const pos = naturalToDisplayedImageCoords(imgEl, coordinate.x_a, coordinate.y_a);
        return (
            <Box
                key={key}
                sx={{
                    position: "absolute",
                    top: pos.y - 5,
                    left: pos.x - 5,
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    bgcolor: "#f44336",
                    border: "1.5px solid #ffffff",
                    pointerEvents: "none",
                }}
            />
        );
    };

    const modalSlot = modalSlotKey != null ? slots[modalSlotKey] : null;
    const markingFilesGenerated = markingRows.length > 0;

    const renderNormBox = (mode) => {
        const norm = norms[mode] ?? {};
        const stored = Boolean(normOnServer[mode]);
        return (
            <Box
                sx={{
                    width: "100%", boxSizing: "border-box", p: 2, borderRadius: 2,
                    border: "1px solid", borderColor: "divider", bgcolor: "grey.50",
                }}
            >
                <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ width: "100%" }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="subtitle2" fontWeight={700}>
                                {`Normalization image — ${modeLabel(mode)}`}
                            </Typography>
                            {stored && <Chip size="small" color="success" label="Uploaded" />}
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {stored
                                ? `Used for every plate photographed in ${modeLabel(mode).toLowerCase()}. Pick a new one to replace it.`
                                : `The flat-field reference taken in ${modeLabel(mode).toLowerCase()}; uploaded once and used for every plate photographed in this mode.`}
                        </Typography>
                        <CameraCapturePanel
                            label="Capture normalization photo"
                            disabled={norm.busy || measuring}
                            onCaptured={(file) => processNormFile(mode, file)}
                            manualUpload={
                                <Button
                                    component="label"
                                    variant="outlined"
                                    size="small"
                                    disableElevation
                                    disabled={norm.busy || measuring}
                                    startIcon={<UploadFileIcon />}
                                >
                                    Manual upload
                                    <input
                                        hidden
                                        accept=".nef, .arw"
                                        type="file"
                                        disabled={norm.busy || measuring}
                                        onChange={(e) => processNormFile(mode, e.target.files?.[0])}
                                    />
                                </Button>
                            }
                        />
                    </Box>
                    <Box
                        sx={{
                            width: 160, flexShrink: 0, aspectRatio: "1", position: "relative",
                            borderRadius: 2, overflow: "hidden",
                            bgcolor: norm.preview ? "grey.100" : "grey.50",
                            border: "1px dashed",
                            borderColor: norm.preview ? "divider" : "text.disabled",
                            display: "flex", alignItems: "center", justifyContent: "center",
                        }}
                    >
                        {norm.busy && (
                            <Box
                                sx={{
                                    position: "absolute", inset: 0, zIndex: 4, display: "flex",
                                    flexDirection: "column", alignItems: "center",
                                    justifyContent: "center", gap: 1, px: 1.5,
                                    bgcolor: "rgba(255, 255, 255, 0.88)", backdropFilter: "blur(4px)",
                                }}
                            >
                                <Typography variant="caption" color="text.secondary" align="center">
                                    Uploading…
                                </Typography>
                                <LinearProgress sx={{ width: "90%" }} />
                            </Box>
                        )}
                        {norm.preview ? (
                            <img
                                src={norm.preview}
                                alt={`Normalization preview ${modeLabel(mode)}`}
                                style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
                            />
                        ) : (
                            <Typography variant="caption" color="text.disabled" sx={{ px: 1.5, textAlign: "center" }}>
                                {stored ? "Loading preview…" : "Preview appears here"}
                            </Typography>
                        )}
                    </Box>
                </Stack>
            </Box>
        );
    };

    /** The photo slot of the plate chosen on the matrix, in one capture mode. */
    const renderPlateSlot = (row, mode) => {
        const key = slotKey(row.batch, row.repetition, mode);
        const slot = slots[key] ?? emptySlotState();
        const capture = captureFor(row, mode);
        const stored = Boolean(capture?.photo);
        const cornerCount = slot.preview ? slot.cornerPoints.length : (capture?.corner_points?.length ?? 0);
        const label = `${plateName(row.batch, row.repetition)} — ${modeLabel(mode)}`;
        const batch = batchRows.find((b) => b.batch === row.batch);
        return (
            <Stack key={key} spacing={2} sx={{ pt: 2.5, borderTop: "1px solid", borderColor: "divider" }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="subtitle2" fontWeight={700} color="text.secondary">{label}</Typography>
                    {batch?.count != null && (
                        <Typography variant="caption" color="text.secondary" data-testid="slot-batch-caption">
                            {`${batch.count} patches${Array.isArray(batch.grid_size) ? ` · ${batch.grid_size[0]} × ${batch.grid_size[1]}` : ""}`}
                        </Typography>
                    )}
                    {capture?.complete ? (
                        <Chip size="small" color="success" label="Uploaded" />
                    ) : stored ? (
                        <Chip size="small" color="primary" variant="outlined" label="Uploaded" />
                    ) : null}
                </Stack>
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {stored
                            ? "The photo is on the server. Click the preview to check or adjust the four corner points; pick a new photo to replace it."
                            : "Capture or pick the ARW/NEF photo of this plate; it is stored right away and the corner crosses are detected on it."}
                    </Typography>
                    <CameraCapturePanel
                        label={stored ? "Replace plate photo" : "Capture plate photo"}
                        disabled={slot.busy || measuring}
                        onCaptured={(file) => processPlateFileForSlot(key, row, mode, file)}
                        manualUpload={
                            <Button
                                component="label"
                                variant="outlined"
                                size="small"
                                disableElevation
                                disabled={slot.busy || measuring}
                                startIcon={<UploadFileIcon />}
                            >
                                {stored ? "Replace by manual upload" : "Manual upload"}
                                <input
                                    hidden
                                    accept=".nef, .arw"
                                    type="file"
                                    disabled={slot.busy || measuring}
                                    onChange={(e) => processPlateFileForSlot(key, row, mode, e.target.files?.[0])}
                                />
                            </Button>
                        }
                    />
                </Box>

                <Box
                    role={slot.preview && !slot.busy ? "button" : undefined}
                    tabIndex={slot.preview && !slot.busy ? 0 : undefined}
                    aria-label={slot.preview && !slot.busy ? "Open full screen to adjust corner points" : undefined}
                    onClick={slot.preview && !slot.busy ? () => setModalSlotKey(key) : undefined}
                    sx={{
                        // An explicit width, not flex: 1: in this column stack
                        // flex: 1 lets the box shrink to a height of 0.
                        width: "100%", boxSizing: "border-box", minWidth: 0, maxWidth: 440, aspectRatio: "1",
                        position: "relative", borderRadius: 2, overflow: "hidden",
                        bgcolor: slot.preview ? "grey.100" : "grey.50",
                        border: "1px dashed",
                        borderColor: slot.preview ? "divider" : "text.disabled",
                        ...(slot.preview && !slot.busy
                            ? {
                                cursor: "pointer",
                                transition: "border-color 0.15s ease, border-style 0.15s ease",
                                "&:hover": { borderColor: "primary.main", borderStyle: "solid" },
                            }
                            : {}),
                    }}
                >
                    {slot.busy && (
                        <Box
                            sx={{
                                position: "absolute", inset: 0, zIndex: 4, display: "flex",
                                flexDirection: "column", alignItems: "center",
                                justifyContent: "center", gap: 1.5, px: 2,
                                bgcolor: "rgba(255, 255, 255, 0.88)",
                                backdropFilter: "blur(4px)",
                            }}
                        >
                            <Typography variant="body2" color="text.secondary" align="center">
                                {slot.previewMessage || "Processing…"}
                            </Typography>
                            <LinearProgress sx={{ width: "min(90%, 280px)" }} />
                        </Box>
                    )}
                    {slot.preview ? (
                        <Box sx={{ position: "absolute", inset: 0 }}>
                            <img
                                ref={(el) => { slotImageRefs.current[key] = el; }}
                                src={slot.preview}
                                alt={`Plate preview ${label}`}
                                style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
                                onLoad={() => setThumbLayoutTick((t) => t + 1)}
                            />
                            {slot.cornerPoints.map((coordinate, i) =>
                                cornerDot(coordinate, slotImageRefs.current[key], i)
                            )}
                        </Box>
                    ) : stored ? (
                        <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ px: 1.5, textAlign: "center" }}>
                                Photo stored on the server
                            </Typography>
                            <Button
                                size="small" variant="outlined" disabled={slot.busy || measuring}
                                onClick={() => { previewRequested.current[key] = false; loadStoredPreview(key, row, mode); }}
                                sx={{ textTransform: "none", borderRadius: 2 }}
                            >
                                Show preview & corners
                            </Button>
                        </Box>
                    ) : (
                        <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Typography variant="caption" color="text.disabled" sx={{ px: 1.5, textAlign: "center" }}>
                                Plate preview appears here
                            </Typography>
                        </Box>
                    )}
                </Box>

                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                    {slot.savingCorners ? (
                        <>
                            <CircularProgress size={16} thickness={5} aria-label="Saving corner points" />
                            <Typography variant="caption" color="text.secondary">Saving corner points…</Typography>
                        </>
                    ) : stored && cornerCount !== 4 ? (
                        <Typography variant="caption" color="text.secondary">
                            {`${cornerCount} of 4 corner points set — open the preview to place them`}
                        </Typography>
                    ) : stored && !normOnServer[mode] ? (
                        <Typography variant="caption" color="text.secondary">
                            {`Normalization image for ${modeLabel(mode).toLowerCase()} still missing`}
                        </Typography>
                    ) : null}
                </Stack>
            </Stack>
        );
    };

    /** One capture mode: its normalization image, the plate matrix and the slot. */
    const renderModePhotos = (mode) => {
        const plate = activePlateOf(mode);
        const done = completeCountOf(mode);
        const allDone = markingRows.length > 0 && done === markingRows.length;
        const otherMode = activeModes.find((m) => m !== mode && completeCountOf(m) < markingRows.length);
        return (
            <Stack spacing={2} sx={{ width: "100%" }}>
                {renderNormBox(mode)}
                <Stack spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="subtitle2" fontWeight={700}>
                            {`Plates — ${modeLabel(mode)}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {`${done} of ${markingRows.length} uploaded · click a plate to open its photo slot`}
                        </Typography>
                    </Stack>
                    <PlateMatrix
                        batches={batchRows}
                        repetitions={repetitions}
                        selected={plate}
                        onSelect={(batch, repetition) => selectPlate(mode, { batch, repetition })}
                        stateFor={plateState(mode)}
                        disabled={measuring}
                        ariaLabel={`Plates ${modeLabel(mode)}`}
                    />
                </Stack>
                {allDone && (
                    <Alert
                        severity="success" sx={{ borderRadius: 2 }}
                        action={otherMode ? (
                            <Button size="small" endIcon={<ArrowForwardIcon />} onClick={() => setPhotoMode(otherMode)} sx={{ textTransform: "none" }}>
                                {modeLabel(otherMode)}
                            </Button>
                        ) : null}
                    >
                        {`Every plate of ${modeLabel(mode).toLowerCase()} is uploaded.`}
                    </Alert>
                )}
                {plate && renderPlateSlot(plate, mode)}
            </Stack>
        );
    };

    const progressPercent = totalSets > 0 ? Math.round((completeCount / totalSets) * 100) : 0;

    return (
        <>
            <Box sx={{ width: "100%", maxWidth: "100%", mt: "2%", mb: 4, boxSizing: "border-box" }}>
                <ExplorationPageShell
                    title="Spatial Repeatability Measurement"
                    subtitle={modeContrastType
                        ? "Mark every batch of the composed palette once per repetition, photograph each plate in both capture modes, check the detected corner crosses, then measure."
                        : "Mark every batch of the composed palette once per repetition, photograph each plate in one or two capture modes, check the detected corner crosses, then measure — or skip the test when the palette cannot be marked now."}
                    noPaper
                    stepSidebar={selectionSidebar("spatial", sampleCount, selectionType)}
                    contentBoxSx={{
                        width: "100%",
                        maxWidth: "100%",
                        boxSizing: "border-box",
                        px: 0,
                        py: { xs: 2, md: 3 },
                    }}
                >
                    <Stack spacing={3} sx={{ width: "100%", maxWidth: "100%", minWidth: 0, mt: 1, alignItems: "stretch" }}>
                        {errorMessage && <Alert severity="error" sx={{ borderRadius: 2 }}>{errorMessage}</Alert>}
                        {selectionStatus === "SPATIAL_MEASURED" && (
                            <Alert
                                severity="info" sx={{ borderRadius: 2 }}
                                action={
                                    <Button size="small" onClick={() => navigate(stepRoute("repeatability", selectionType))} sx={{ textTransform: "none" }}>
                                        Open the filter
                                    </Button>
                                }
                            >
                                This palette has been measured already. Replacing a photo and pressing Next measures again and starts the filter over.
                            </Alert>
                        )}

                        {/* Step 1 — marking */}
                        <Paper elevation={0} sx={stepCardSx}>
                            <StepHeader number={1} title="Mark the repeatability plates">
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    The palette is marked in batches of at most 10 × 10 patches (one camera field),
                                    each batch once per repetition. Every batch and repetition is one marking file,
                                    labelled [B&lt;batch&gt;][R&lt;repetition&gt;] on the metal. Download the files
                                    for the laser software.
                                </Typography>
                                {preparing && (
                                    <Box sx={{ width: "100%", mt: 2 }}>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                            Preparing the marking files for the composed palette…
                                        </Typography>
                                        <LinearProgress sx={{ maxWidth: 420 }} />
                                    </Box>
                                )}
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        disableElevation
                                        disabled={generating || preparing}
                                        startIcon={<DownloadIcon />}
                                        onClick={generateAndDownloadZip}
                                        sx={actionButtonSx}
                                    >
                                        Download marking files (ZIP)
                                    </Button>
                                    {generating ? (
                                        <CircularProgress size={20} thickness={4} aria-label="Generating marking files" />
                                    ) : null}
                                    <MarkingTimeChip
                                        value={sumMarkingTimes(markingRows.map((row) => row.predicted_marking_time))}
                                        label={markingRows.length > 1 ? `All ${markingRows.length} plates` : "Predicted marking time"}
                                    />
                                </Stack>
                                {markingFilesGenerated && (
                                    // Remote marking: the whole section (plate picker + laser
                                    // controls) exists only while the laser software runs on this computer.
                                    <LaserMarkPanel
                                        getMarkingBlob={getMarkingBlobForLaser}
                                        predictedMarkingTime={(plateRow(markPlate) ?? markingRows[0])?.predicted_marking_time}
                                    >
                                        <Stack spacing={1.5} sx={{ width: "100%", mb: 1.5 }}>
                                            <Typography variant="subtitle2" fontWeight={700}>
                                                Plate to mark on the laser
                                            </Typography>
                                            <PlateMatrix
                                                batches={batchRows}
                                                repetitions={repetitions}
                                                selected={markPlate}
                                                onSelect={(batch, repetition) => setMarkPlate({ batch, repetition })}
                                                ariaLabel="Plate to mark on the laser"
                                                tileTestId="mark-tile"
                                            />
                                        </Stack>
                                    </LaserMarkPanel>
                                )}
                            </StepHeader>
                        </Paper>

                        {/* Step 2 — capture settings */}
                        {markingFilesGenerated && (
                            <Paper elevation={0} sx={stepCardSx}>
                                <StepHeader number={2} title="Capture settings">
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                        The plates can be photographed in one capture mode or in two. With two
                                        modes every plate needs a photo set per mode (its own normalization
                                        image and plate photos), and a colour is kept only when its repetitions
                                        agree within the acceptable ΔE00 in <strong>both</strong> modes. The
                                        acceptable ΔE00 per mode is chosen on the next page.
                                    </Typography>
                                    <RadioGroup
                                        value={modes}
                                        onChange={(e) => setModes(Number(e.target.value))}
                                        aria-label="Number of capture modes"
                                        sx={{ mt: 1.5 }}
                                    >
                                        <FormControlLabel value={1} control={<Radio size="small" />} label="One capture mode" disabled={measuring || modeContrastType} />
                                        <FormControlLabel value={2} control={<Radio size="small" />} label="Two capture modes" disabled={measuring} />
                                    </RadioGroup>
                                    {modeContrastType && (
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            The Mode Contrast Selection is captured in two modes.
                                        </Typography>
                                    )}
                                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                                        <Button
                                            variant={settingsDirty ? "contained" : "outlined"}
                                            size="small"
                                            disableElevation
                                            startIcon={<SaveIcon />}
                                            disabled={savingSettings || !settingsDirty || measuring}
                                            onClick={saveSettings}
                                            sx={actionButtonSx}
                                        >
                                            Save
                                        </Button>
                                        {savingSettings ? <CircularProgress size={20} thickness={4} aria-label="Saving settings" /> : null}
                                        <Typography variant="caption" color="text.secondary">
                                            {settingsDirty
                                                ? "Unsaved — the photo sets below follow the saved setting."
                                                : `Saved: ${savedSettings.modes} capture mode${savedSettings.modes > 1 ? "s" : ""}.`}
                                        </Typography>
                                    </Stack>
                                </StepHeader>
                            </Paper>
                        )}

                        {/* Step 3 — photos: one tab per capture mode, one slot at a time */}
                        {markingFilesGenerated && (
                            <Paper elevation={0} sx={stepCardSx}>
                                <Stack spacing={2} sx={{ width: "100%" }}>
                                    <StepHeader number={3} title="Plate & normalization photos">
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                            {savedSettings.modes > 1
                                                ? "Per capture mode: the normalization image taken in that mode, then one ARW/NEF photo per plate."
                                                : "The normalization image, then one ARW/NEF photo per plate."}
                                            {" Every photo goes to the server once, when you pick it; the corner crosses are detected on the stored file and can be corrected in the preview. After an upload the slot moves on to the next plate."}
                                        </Typography>
                                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1.5 }}>
                                            <LinearProgress
                                                variant="determinate"
                                                value={progressPercent}
                                                aria-label="Photo sets uploaded"
                                                sx={{ flex: 1, maxWidth: 420, height: 8, borderRadius: 4 }}
                                            />
                                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                                                {`${completeCount} of ${totalSets} photo sets uploaded.`}
                                            </Typography>
                                        </Stack>
                                    </StepHeader>
                                    {savedSettings.modes > 1 && (
                                        <Tabs
                                            value={activeModes.includes(photoMode) ? photoMode : 1}
                                            onChange={(_e, value) => setPhotoMode(Number(value))}
                                            aria-label="Capture mode"
                                            sx={{
                                                borderBottom: 1,
                                                borderColor: "divider",
                                                "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: "0.9rem" },
                                            }}
                                        >
                                            {activeModes.map((mode) => (
                                                <Tab
                                                    key={mode}
                                                    value={mode}
                                                    label={`${modeLabel(mode)} · ${completeCountOf(mode)}/${markingRows.length}`}
                                                />
                                            ))}
                                        </Tabs>
                                    )}
                                    {renderModePhotos(activeModes.includes(photoMode) ? photoMode : 1)}
                                </Stack>
                            </Paper>
                        )}

                        {/* Optional: finish without the test */}
                        {selectionStatus !== "SPATIAL_MEASURED" && !modeContrastType && (
                            <Paper elevation={0} sx={{ ...stepCardSx, borderStyle: "dashed" }}>
                                <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ width: "100%" }}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="subtitle1" fontWeight={700}>
                                            Palette not marked
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                            The spatial repeatability test is optional. Skipping it finalizes the
                                            selection with the composed palette; no per-sample ΔE00 repeatability
                                            is recorded, and the palette export and marking file become available
                                            immediately. This cannot be undone.
                                        </Typography>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<SkipNextIcon />}
                                            disabled={measuring || skipping}
                                            onClick={() => setSkipDialogOpen(true)}
                                            sx={{ ...actionButtonSx, mt: 2 }}
                                        >
                                            Skip repeatability test
                                        </Button>
                                    </Box>
                                </Stack>
                            </Paper>
                        )}

                        {markingFilesGenerated && (
                            <Typography variant="body2" color="text.secondary">
                                {readyToMeasure
                                    ? (measuring
                                        ? "Measuring every plate in every capture mode…"
                                        : "Next measures the repeatability (ΔE00 between the repetitions, per mode) and opens the Spatial Repeatability Filter, where the acceptable ΔE00 is set and further patches can be excluded manually before the palette is reduced.")
                                    : `${totalSets - completeCount} photo set${totalSets - completeCount === 1 ? "" : "s"} still required before the repeatability can be measured.`}
                            </Typography>
                        )}
                        <NavBarBottom
                            variant="embedded"
                            returnLabel="Back"
                            prevPage={stepRoute("prepass", selectionType)}
                            nextPage={stepRoute("repeatability", selectionType)}
                            // Next measures, then opens the review.
                            enableNextPage={!readyToMeasure || measuring}
                            nextLoading={measuring}
                            nextPageClickFunc={measureSpatialRepeatability}
                        />
                    </Stack>
                </ExplorationPageShell>
            </Box>

            <Dialog open={skipDialogOpen} onClose={() => !skipping && setSkipDialogOpen(false)}>
                <DialogTitle>Skip the repeatability test?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        The selection is finished with the composed palette
                        {sampleCount != null ? ` (${sampleCount} samples)` : ""} and becomes read-only.
                        No spatial repeatability ΔE00 will be measured for these samples.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setSkipDialogOpen(false)}
                        disabled={skipping}
                        sx={{ textTransform: "none", borderRadius: 2 }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disableElevation
                        onClick={skipSpatialRepeatability}
                        disabled={skipping}
                        startIcon={skipping ? <CircularProgress size={16} thickness={5} /> : <SkipNextIcon />}
                        sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
                    >
                        Skip and finish
                    </Button>
                </DialogActions>
            </Dialog>

            {/* full-screen corner correction modal; the corners are saved on Done */}
            <Modal open={modalSlotKey != null} onClose={closeModal}>
                <MuiBox
                    sx={{
                        position: 'absolute', top: '50%', left: '50%',
                        transform: 'translate(-50%, -50%)', width: '85%', height: '88%',
                        bgcolor: 'background.paper', boxShadow: 24, p: 2,
                        borderRadius: 2, display: "flex", flexDirection: "column",
                    }}
                >
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ flexGrow: 1 }}>
                            {modalRow
                                ? `Corner points — ${plateName(modalRow.batch, modalRow.repetition)} — ${modeLabel(modalMode)}`
                                : "Corner points"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {`${modalSlot?.cornerPoints?.length ?? 0} of 4 — click to place: top-left, top-right, bottom-left, bottom-right`}
                        </Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            disabled={autoCornersBusy}
                            onClick={handleAutomaticCornerDetection}
                            sx={{ textTransform: "none", borderRadius: 2 }}
                        >
                            Automatic detection
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => modalSlotKey != null && updateSlot(modalSlotKey, { cornerPoints: [] })}
                            sx={{ textTransform: "none", borderRadius: 2 }}
                        >
                            Clear corners
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            disableElevation
                            onClick={closeModal}
                            sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
                        >
                            Done
                        </Button>
                    </Stack>
                    {autoCornersBusy && <LinearProgress sx={{ mb: 1 }} />}
                    {modalSlot?.preview && (
                        <Box sx={{ position: "relative", flex: 1, minHeight: 0 }}>
                            <img
                                src={modalSlot.preview}
                                ref={modalImageRef}
                                alt="Marked plate full screen"
                                style={{ width: "100%", height: "100%", objectFit: "contain", cursor: "crosshair", display: "block" }}
                                onClick={handleModalImageClick}
                                onLoad={() => setModalLayoutTick((t) => t + 1)}
                            />
                            {modalSlot.cornerPoints.map((coordinate, index) =>
                                cornerDot(coordinate, modalImageRef.current, index)
                            )}
                        </Box>
                    )}
                </MuiBox>
            </Modal>
        </>
    );
}
